import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import type {
  LinkedEventOption,
  LinkedEventOptionsResponse,
} from "@/content-hub/linked-events/types";
import {
  getDefaultAuthToken,
  getOrganizationExternalId,
  getSpaceExternalId,
} from "@/content-hub/store/selectors";
import useDebouncedValue from "@/hooks/use-debounced-value";
import { useAppSelector } from "@/redux/store";
import logger from "@/utils/logger";

export const EVENT_OPTIONS_PAGE_SIZE = 20;

const DEFAULT_PAGE_INFO: LinkedEventOptionsResponse["pageInfo"] = {
  hasNextPage: false,
  endCursor: null,
};

export type EventOptionsLoaderStatus = "idle" | "processing" | "failed";

export type EventOptionsLoader = {
  options: LinkedEventOption[];
  pageInfo: LinkedEventOptionsResponse["pageInfo"];
  status: EventOptionsLoaderStatus;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  loadMore: () => void;
};

type EventOptionsCacheEntry = {
  options: LinkedEventOption[];
  pageInfo: LinkedEventOptionsResponse["pageInfo"];
};

// Shared across hook instances so opening one picker after another reuses the
// last successful response for the same request key (org + auth + query).
const eventOptionsCache = new Map<string, EventOptionsCacheEntry>();

export const clearEventOptionsCache = () => {
  eventOptionsCache.clear();
};

const mergeEventOptions = (
  existingOptions: LinkedEventOption[],
  nextOptions: LinkedEventOption[]
) => {
  const optionMap = new Map(
    existingOptions.map((option) => [option.id, option])
  );

  nextOptions.forEach((option) => optionMap.set(option.id, option));

  return Array.from(optionMap.values());
};

export const useEventOptionsLoader = ({
  enabled = true,
  loggerDetails,
}: {
  enabled?: boolean;
  loggerDetails: string;
}): EventOptionsLoader => {
  const organizationExternalId = useAppSelector(getOrganizationExternalId);
  const spaceExternalId = useAppSelector(getSpaceExternalId);
  const defaultAuthToken = useAppSelector(getDefaultAuthToken);
  const { preEventOrganizationSpaces: spacesEnabled } = useFlags();
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState<LinkedEventOption[]>([]);
  const [pageInfo, setPageInfo] = useState<
    LinkedEventOptionsResponse["pageInfo"]
  >(DEFAULT_PAGE_INFO);
  const [status, setStatus] = useState<EventOptionsLoaderStatus>("idle");
  const activeControllerRef = useRef<AbortController | null>(null);
  const loadedRequestKeyRef = useRef<string | null>(null);
  const lastRequestFailedRef = useRef(false);
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 250);

  const getRequestKey = useCallback(
    (query: string) =>
      `${organizationExternalId ?? ""}:${defaultAuthToken ?? ""}:${
        spacesEnabled ? spaceExternalId ?? "" : ""
      }:${query}`,
    [defaultAuthToken, organizationExternalId, spaceExternalId, spacesEnabled]
  );

  const fetchEventOptions = useCallback(
    ({
      query,
      after,
      append = false,
    }: {
      query: string;
      after?: string | null;
      append?: boolean;
    }) => {
      if (!organizationExternalId || !defaultAuthToken) return;

      activeControllerRef.current?.abort();

      const controller = new AbortController();
      activeControllerRef.current = controller;

      const queryParams = new URLSearchParams();
      queryParams.append("organization_id", organizationExternalId);
      queryParams.append("first", String(EVENT_OPTIONS_PAGE_SIZE));

      if (spaceExternalId && spacesEnabled) {
        queryParams.append("space_external_id", spaceExternalId);
      }

      if (query) {
        queryParams.append("query", query);
      }

      if (after) {
        queryParams.append("after", after);
      }

      setStatus("processing");

      fetch(`/canvas/api/content/event-options?${queryParams}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${defaultAuthToken}`,
          "Content-Type": "application/json",
        },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch event options");
          }

          return response.json();
        })
        .then((response: LinkedEventOptionsResponse) => {
          if (controller.signal.aborted) return;

          const requestKey = getRequestKey(query);
          const nextPageInfo = response.pageInfo ?? DEFAULT_PAGE_INFO;
          const responseData = response.data ?? [];

          if (append) {
            setOptions((currentOptions) => {
              const merged = mergeEventOptions(currentOptions, responseData);
              eventOptionsCache.set(requestKey, {
                options: merged,
                pageInfo: nextPageInfo,
              });
              return merged;
            });
          } else {
            eventOptionsCache.set(requestKey, {
              options: responseData,
              pageInfo: nextPageInfo,
            });
            setOptions(responseData);
          }

          setPageInfo(nextPageInfo);
          loadedRequestKeyRef.current = requestKey;
          lastRequestFailedRef.current = false;
          setStatus("idle");
        })
        .catch((error) => {
          if (controller.signal.aborted) return;

          lastRequestFailedRef.current = true;
          setStatus("failed");
          setPageInfo(DEFAULT_PAGE_INFO);

          if (!append) {
            setOptions([]);
          }

          logger.error(error, {
            details: loggerDetails,
          });
        });
    },
    [
      defaultAuthToken,
      getRequestKey,
      loggerDetails,
      organizationExternalId,
      spaceExternalId,
      spacesEnabled,
    ]
  );

  useEffect(() => {
    return () => activeControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!enabled || !organizationExternalId || !defaultAuthToken) return;

    const query = debouncedSearchQuery.trim();
    const requestKey = getRequestKey(query);

    if (
      loadedRequestKeyRef.current === requestKey &&
      !lastRequestFailedRef.current
    ) {
      return;
    }

    const cached = eventOptionsCache.get(requestKey);
    if (cached) {
      setOptions(cached.options);
      setPageInfo(cached.pageInfo);
      loadedRequestKeyRef.current = requestKey;
      lastRequestFailedRef.current = false;
      setStatus("idle");
      return;
    }

    fetchEventOptions({ query });
  }, [
    debouncedSearchQuery,
    defaultAuthToken,
    enabled,
    fetchEventOptions,
    getRequestKey,
    organizationExternalId,
  ]);

  const loadMore = useCallback(() => {
    if (!pageInfo.endCursor || status === "processing") return;

    fetchEventOptions({
      query: debouncedSearchQuery.trim(),
      after: pageInfo.endCursor,
      append: true,
    });
  }, [debouncedSearchQuery, fetchEventOptions, pageInfo.endCursor, status]);

  return {
    options,
    pageInfo,
    status,
    searchQuery,
    setSearchQuery,
    loadMore,
  };
};

export const createSelectedOption = (
  eventExternalId: string,
  title?: string | null
): LinkedEventOption => ({
  id: eventExternalId,
  slug: "",
  name: title || eventExternalId,
  status: null,
  expired: false,
  isLite: false,
  venueType: null,
  description: null,
  timeStart: null,
  timeEnd: null,
  timezone: null,
  imageUrl: null,
  fullImageUrl: null,
  logoUrl: null,
  fullLogoUrl: null,
  registrationPageUrl: null,
});
